/* ======================================
   Maison d'Etto v2 — <collection-filter>
   Client-side filtering and ranking of the cards already on the page.
   - two <v2-chip-strip>s, both single choice: [data-chip-group="type"] (chips
     carry data-filter-tags / data-filter-types, any-of, handleized) and
     [data-chip-group="family"] (chip key is the linesheet word, handleized).
     One of each may be pressed at once; they combine
   - cards carry data-tags, data-type, data-lead (one word) and data-also (words)
   - the type chip filters. The family chip shows every card that carries the
     word, in one grid, the cards that LEAD with it first (WS 12b lead-first,
     without the split headings — site owner, 15 Sep)
   - chips that match nothing given the other group's choice are dimmed; tapping a
     dimmed chip clears the other group and starts over from it (never gates)
   - the editorial seed cards keep their positions in the filtered grid too
   - family chips that match nothing at all on this page are hidden (outside the
     theme editor)
   - state is mirrored into the URL (?type=…&family=…)
   - the count label comes from an inline <script type="application/json" data-v2-config>
   ====================================== */
(function () {
  'use strict';

  var split = function (str) { return (str || '').split('|').filter(Boolean); };

  class V2CollectionFilter extends HTMLElement {
    connectedCallback() {
      /* the strips must be upgraded before we talk to them */
      var self = this;
      customElements.whenDefined('v2-chip-strip').then(function () { if (self.isConnected) self.init(); });
    }

    init() {
      var cfgEl = this.querySelector('[data-v2-config]');
      try { this.cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {}; } catch (err) { this.cfg = {}; }

      this.typeStrip = this.querySelector('[data-chip-group="type"]');
      this.familyStrip = this.querySelector('[data-chip-group="family"]');
      this.items = [...this.querySelectorAll('.v2-collection__item')];
      this.seeds = [...this.querySelectorAll('[data-seed]')];
      this.grid = this.querySelector('.v2-card-grid');
      this.countEls = [...this.querySelectorAll('[data-filter-count]')];
      this.emptyEl = this.querySelector('[data-filter-empty]');
      if ((!this.typeStrip && !this.familyStrip) || !this.grid) return;

      this.state = { type: null, family: null };
      this.onChips = this.handleChips.bind(this);
      this.addEventListener('v2:chips', this.onChips);

      this.hideEmptyFamilyChips();
      this.restore();
    }

    disconnectedCallback() {
      this.removeEventListener('v2:chips', this.onChips);
    }

    /* ---------- state in, state out ---------- */

    restore() {
      var params = new URLSearchParams(location.search);
      var type = params.get('type');
      if (type && this.typeChip(type)) this.state.type = type;
      var fam = (params.get('family') || '').split(',')[0];
      if (fam && this.familyStrip && this.familyStrip.find(fam)) this.state.family = fam;
      if (this.typeStrip) this.typeStrip.set(this.state.type ? [this.state.type] : []);
      if (this.familyStrip) this.familyStrip.set(this.state.family ? [this.state.family] : []);
      this.render(false);
    }

    typeChip(key) {
      return this.typeStrip ? this.typeStrip.find(key) : null;
    }

    handleChips(e) {
      var d = e.detail;
      var group = d.strip.dataset.chipGroup;
      var unavailable = d.pressed && d.chip.dataset.chip !== 'all' && d.chip.classList.contains('is-unavailable');
      if (d.chip.dataset.chip === 'all' && group === 'type') {
        /* View All is one tap back to the full list: it clears both groups */
        this.state = { type: null, family: null };
        if (this.familyStrip) this.familyStrip.set([]);
      } else if (group === 'type') {
        if (unavailable && this.familyStrip) { this.state.family = null; this.familyStrip.set([]); }
        this.state.type = d.keys[0] || null;
      } else {
        if (unavailable && this.typeStrip) { this.state.type = null; this.typeStrip.set([]); }
        this.state.family = d.keys[0] || null;
      }
      this.render(true);
    }

    /* ---------- matching ---------- */

    typeMatches(item, key) {
      var chip = this.typeChip(key);
      if (!chip) return true;
      var tags = split(chip.dataset.filterTags);
      var types = split(chip.dataset.filterTypes);
      var itemTags = split(item.dataset.tags);
      var tagOk = !tags.length || tags.some(function (t) { return itemTags.indexOf(t) !== -1; });
      var typeOk = !types.length || types.indexOf(item.dataset.type || '') !== -1;
      return tagOk && typeOk;
    }

    carries(item, key) {
      return item.dataset.lead === key || split(item.dataset.also).indexOf(key) !== -1;
    }

    /* the cards a family chip returns from a base set: leads first, then the rest, each in collection order */
    rank(base, key) {
      if (!key) return base;
      var lead = base.filter(function (i) { return i.dataset.lead === key; });
      var present = base.filter(function (i) { return i.dataset.lead !== key && split(i.dataset.also).indexOf(key) !== -1; });
      return lead.concat(present);
    }

    /* ---------- render ---------- */

    render(pushUrl) {
      var self = this;
      var base = this.items.filter(function (i) { return !self.state.type || self.typeMatches(i, self.state.type); });
      var visible = this.rank(base, this.state.family);
      var shown = {};
      visible.forEach(function (i) { shown[i.dataset.index] = true; });

      /* one grid: ranked cards first in their new order, hidden cards after */
      var frag = document.createDocumentFragment();
      visible.forEach(function (i) { frag.appendChild(i); });
      this.items.forEach(function (i) { if (!shown[i.dataset.index]) frag.appendChild(i); });
      this.grid.appendChild(frag);
      this.items.forEach(function (i) { i.hidden = !shown[i.dataset.index]; });

      /* seed cards sit after the Nth visible card in whatever the grid shows, filtered or not,
         and hide when there are fewer cards than their position (site owner, 15 Sep) */
      this.seeds.forEach(function (seed) {
        var pos = parseInt(seed.dataset.position, 10) || 0;
        if (pos === 0) { self.grid.prepend(seed); seed.hidden = false; return; }
        var anchor = visible[pos - 1];
        if (anchor) { anchor.after(seed); seed.hidden = false; } else { seed.hidden = true; }
      });

      /* View All means no filter at all — it stays pressed only while both groups are empty */
      var all = this.typeStrip ? this.typeStrip.find('all') : null;
      if (all) all.setAttribute('aria-pressed', (!this.state.type && !this.state.family) ? 'true' : 'false');

      this.updateCount(visible.length);
      this.updateAvailability(base);
      if (this.emptyEl) this.emptyEl.hidden = visible.length > 0;

      if (pushUrl) {
        var url = new URL(location.href);
        ['filter', 'type', 'family', 'either'].forEach(function (p) { url.searchParams.delete(p); });
        if (this.state.type) url.searchParams.set('type', this.state.type);
        if (this.state.family) url.searchParams.set('family', this.state.family);
        history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
      }
      if (window.V2Reveal) window.V2Reveal(this);
    }

    /* dim any chip that, combined with the other group's choice, shows nothing */
    updateAvailability(base) {
      var self = this;
      if (this.typeStrip) {
        this.typeStrip.chips.forEach(function (chip) {
          var key = chip.dataset.chip;
          if (key === 'all') return;
          var set = self.items.filter(function (i) { return self.typeMatches(i, key); });
          var any = !self.state.family || set.some(function (i) { return self.carries(i, self.state.family); });
          self.mark(chip, any);
        });
      }
      if (this.familyStrip) {
        this.familyStrip.chips.forEach(function (chip) {
          var key = chip.dataset.chip;
          if (key === 'all') return; /* View All can sit in this bar on single-type collections */
          var any = base.some(function (i) { return self.carries(i, key); });
          self.mark(chip, any);
        });
      }
    }

    mark(chip, available) {
      chip.classList.toggle('is-unavailable', !available);
      if (available) chip.removeAttribute('aria-disabled'); else chip.setAttribute('aria-disabled', 'true');
    }

    /* a family chip no card on this page carries is hidden, unless we are in the editor */
    hideEmptyFamilyChips() {
      if (!this.familyStrip || document.documentElement.classList.contains('v2-in-editor')) return;
      var self = this;
      var left = 0;
      var hasAll = false;
      [...this.familyStrip.querySelectorAll('[data-chip]')].forEach(function (chip) {
        if (chip.dataset.chip === 'all') { hasAll = true; return; }
        var any = self.items.some(function (i) { return self.carries(i, chip.dataset.chip); });
        chip.hidden = !any;
        if (any) left++;
      });
      /* the row goes only when nothing is left in it — View All and the count keep it */
      var row = this.familyStrip.closest('.v2-chips');
      if (row) row.hidden = left === 0 && !hasAll;
      this.familyStrip.sync();
    }

    updateCount(n) {
      if (!this.countEls.length) return;
      var label = this.cfg.count_label || '';
      var text = n + (label ? ' ' + label : '');
      this.countEls.forEach(function (el) { el.textContent = text; });
    }
  }

  if (!customElements.get('collection-filter')) {
    customElements.define('collection-filter', V2CollectionFilter);
  }
})();
